import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Guide, GuideStep } from './types';
import { findGuide } from './catalog';

interface GuideContextValue {
  guide: Guide | null;
  stepIndex: number;
  step: GuideStep | null;
  isActive: boolean;
  start: (guideId: string) => void;
  next: () => void;
  back: () => void;
  stop: () => void;
}

const GuideContext = createContext<GuideContextValue | undefined>(undefined);

/**
 * Holds the active walkthrough. Steps that carry a `route` are skipped when
 * the user is already on that page; otherwise the step is shown and clicking
 * its target (the sidebar link) both navigates and advances.
 */
export const GuideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [guide, setGuide] = useState<Guide | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const stop = useCallback(() => { setGuide(null); setStepIndex(0); }, []);

  // Skip "go to page" steps whose route is already current.
  const resolveIndex = useCallback((g: Guide, from: number, pathname: string): number => {
    let i = from;
    while (i < g.steps.length && g.steps[i].route && pathname.startsWith(g.steps[i].route as string)) i += 1;
    return i;
  }, []);

  const start = useCallback((guideId: string) => {
    const g = findGuide(guideId);
    if (!g) return;
    setGuide(g);
    setStepIndex(resolveIndex(g, 0, location.pathname));
  }, [location.pathname, resolveIndex]);

  const next = useCallback(() => {
    if (!guide) return;
    const current = guide.steps[stepIndex];
    // A route step advanced via its Next button (not a click on the link): navigate for them.
    if (current?.route && !location.pathname.startsWith(current.route)) navigate(current.route);
    const i = resolveIndex(guide, stepIndex + 1, current?.route || location.pathname);
    if (i >= guide.steps.length) stop(); else setStepIndex(i);
  }, [guide, stepIndex, location.pathname, navigate, resolveIndex, stop]);

  const back = useCallback(() => {
    if (!guide) return;
    let i = stepIndex - 1;
    while (i >= 0 && guide.steps[i].route && location.pathname.startsWith(guide.steps[i].route as string)) i -= 1;
    setStepIndex(Math.max(0, i));
  }, [guide, stepIndex, location.pathname]);

  // If the page changes while on a route step (user clicked the link), move on.
  useEffect(() => {
    if (!guide) return;
    const current = guide.steps[stepIndex];
    if (current?.route && location.pathname.startsWith(current.route)) {
      const i = resolveIndex(guide, stepIndex + 1, location.pathname);
      if (i >= guide.steps.length) stop(); else setStepIndex(i);
    }
  }, [location.pathname, guide, stepIndex, resolveIndex, stop]);

  const value = useMemo<GuideContextValue>(() => ({
    guide,
    stepIndex,
    step: guide ? guide.steps[stepIndex] ?? null : null,
    isActive: !!guide,
    start, next, back, stop,
  }), [guide, stepIndex, start, next, back, stop]);

  return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
};

export const useGuide = (): GuideContextValue => {
  const ctx = useContext(GuideContext);
  if (!ctx) throw new Error('useGuide must be used within a GuideProvider');
  return ctx;
};
